/**
 * useLiveChat — le chat du live de l'élève : un fil par tuteur du roster (celui de la séance
 * d'abord), et « Tous » dans un cours partagé (`chatChannels`).
 *
 * Les mêmes sources qu'aujourd'hui (`common/liveMode/LiveChat.js`, `common/messenger/CommonChat.js`) :
 *  - le fil : `thread_ids[tuteur]` (privé, un par tuteur du roster, comme les vignettes de
 *    l'ancien chat) ou `thread_id` (le groupe) de la séance ;
 *  - les messages : `GET api/messenger/threads/{id}/messages` (rangés par le réducteur dans
 *    `currentUser.messages[fil]`), puis « lus » (`mark-read`) ;
 *  - l'envoi : `POST …/messages` (`message`, `temporary_id`), puis l'événement `newMessage` sur le
 *    canal de la salle, au tuteur ou à tous — le même format, que le tuteur lit avec l'ancien code ;
 *  - l'arrivée : l'événement `newMessage` d'un autre, ajouté au fil ouvert ;
 *  - la frappe : `typing`, au même destinataire.
 *
 * Les pièces jointes, dans le fil du tuteur, comme `CommonChat` : une photo (`postImage`) ou un
 * document (`postDocument`), envoyés en multipart (l'écran prépare le formulaire), puis l'événement
 * `newMessage` qui porte la ressource rangée par le serveur, au tuteur seul ; une pièce qui ne part
 * pas se voit, et « Renvoyer » la renvoie. Un vocal passe par la file persistée des vocaux de la
 * messagerie (`voiceOutbox`, que l'écran donne) : le même envoi (`postAudio`), mais il survit à l'app
 * tuée et au live quitté, se voit « Envoi... » puis « Non envoyé » et se relance ; parti pendant que
 * le live est monté, sa ressource s'annonce au tuteur par le même `newMessage`. Parti ailleurs (la
 * messagerie, une relance au démarrage), le tuteur le lit au relevé du fil. Une pièce que le tuteur
 * annonce (la ressource du serveur, sans `_id`) entre dans le fil à l'arrivée. « X écrit… » suit son
 * `typing` (effacé après `TYPING_THROTTLE_MS`), et les messages plus anciens se chargent page par
 * page (`getPageMessages`, `meta.next_page_id`), comme la messagerie.
 *
 * Ce qui change : un message qui ne part pas se voit (« Non envoyé ») au lieu de rester en
 * suspens, avec ce que le serveur a dit d'un refus (`failure`) ; un message que le serveur a rangé
 * est parti, même si la relecture du fil qui suit échoue (il prend la forme du serveur, et ne se
 * renvoie plus en double) ; `newMessage` part une fois le message enregistré (le tuteur qui relit le fil le trouve),
 * et seulement si la salle est connectée — sinon le serveur l'a, le tuteur le lira au relevé ; la
 * frappe part au plus une fois par seconde et demie (le délai d'effacement côté tuteur), plus à
 * chaque touche. Le compteur de non-lus reste celui du réducteur (les écouteurs globaux l'incrémentent,
 * `mark-read` le remet à zéro pour son fil) ; la pastille de la barre compte les fils que les puces
 * montrent (`chatUnreadTotal`), et le chat s'ouvre sur le fil qui a des non-lus (`chatChannelToOpen`) ; un message reçu chat ouvert est dit lu à l'arrivée, puis encore en quittant
 * le fil (l'ancien chat ne le disait qu'à l'ouverture du fil). Un fil n'est dit lu que si le chat est
 * ouvert sur lui : un vocal ou un texte qui finit de partir chat fermé, ou fil quitté, ne le lit pas.
 *
 * **Le fil du groupe se lit sans s'écrire** (`readOnly`), dans le moteur et pas seulement à l'écran :
 * l'envoi d'un texte, d'une pièce ou d'un vocal, et « Renvoyer », y sont refusés, comme la frappe.
 * Le tuteur de la séance sans fil privé ouvre ce fil : sa puce aussi se lit seulement. Une pièce ou un
 * vocal part dans le fil où le geste a commencé (`thread`), pas dans celui ouvert à la fin : un
 * toucher de notification pendant l'enregistrement envoyait le vocal dans « Tous ».
 *
 * Un message reçu par le canal reste tant que le fil relu ne le porte pas : le tuteur annonce son
 * message en même temps qu'il l'envoie, et la relecture peut revenir sans lui. Le brouillon est
 * propre à chaque fil.
 */
import { decode } from "html-entities"
import moment from "moment"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { messengerService } from "../../../data/services/messengerService"
import { apiCall, apiFailureOf, type ApiFailure } from "../../shared/apiCall"
import { type EngineTelemetry } from "../../shared/telemetry"
import { useVoiceOutbox, type VoiceFile, type VoiceOutbox, type VoiceOutboxItem } from "../../voice/voiceOutbox"
import { type LiveDataEvent } from "../room/dataChannel"
import { type SendPolicy, type SendResult } from "../room/roomSender"
import { chatChannels, chatThreadOf, type LiveChatChannel, type LiveChatSession } from "./liveChatChannels"

export {
    chatChannels,
    chatChannelToOpen,
    chatThreadOf,
    chatUnreadTotal,
    GROUP_CHANNEL,
    resolveChatChannel,
    sharedCourseOf,
    type LiveChatChannel,
    type LiveChatChannelEntry,
    type LiveChatSession,
} from "./liveChatChannels"

/** Un message, dans la forme de la messagerie (`MessageResource`), plus son état d'envoi. */
export type LiveChatMessage = {
    id: string
    thread_id?: string | number
    owner_id?: string | number
    body?: string | null
    created_at?: string | null
    image?: { lg?: string } | null
    audio?: string | null
    document?: string | null
    /** Le nom d'un document : la messagerie le sert dans `document_name`. */
    document_name?: string | null
    /** Un envoi de l'élève pas encore enregistré, ou refusé. */
    status?: "sending" | "failed"
    /**
     * Un envoi refusé : ce que le serveur en a dit (son code, son motif). L'écran en tire le motif
     * et si « Renvoyer » a un sens (`messengerFailure`, la règle de la messagerie) : un refus du
     * serveur ne passera pas mieux au second essai. Absent : une coupure, ou rien de rangé.
     */
    failure?: ApiFailure
}

export type UseLiveChatOptions = {
    session: LiveChatSession | null
    user: { id?: number | string | null; display_name?: string | null } | null
    open: boolean
    channel: LiveChatChannel
    send: (type: string, data: Record<string, unknown>, to: number[] | null, policy: SendPolicy) => Promise<SendResult>
    telemetry: EngineTelemetry
    /** La file des vocaux, celle de la messagerie : le moteur ne connaît pas son envoi. */
    voiceOutbox: LiveChatVoiceOutbox
}

/** Où part un vocal : le fil, et le nom du fichier, fixé au geste (la cible de la file de la messagerie). */
export type LiveChatVoiceTarget = { threadId: string; fileName: string }

/** La file des vocaux vue du chat : sa réponse est la ressource du message, annoncée telle quelle. */
export type LiveChatVoiceOutbox = VoiceOutbox<LiveChatVoiceTarget, unknown>

export type LiveChatController = {
    threadId: string | null
    messages: LiveChatMessage[]
    /**
     * Le fil ouvert n'a encore rien de la messagerie : son premier relevé court (une puce touchée
     * pour la première fois). Le fil montre sa forme, pas « Aucun message ».
     */
    loading: boolean
    /**
     * Le fil ouvert se lit sans s'écrire : le fil du groupe (« Tous », ou la puce du tuteur de la
     * séance qui n'a pas de fil privé). Le moteur refuse alors tout envoi ; l'écran lit ce prédicat.
     */
    readOnly: boolean
    /** Le brouillon du fil ouvert : chaque fil garde le sien. */
    draft: string
    setDraft: (text: string) => void
    sendMessage: () => void
    sending: boolean
    /** Un `newMessage` reçu par la salle : le crochet du live le passe ici. */
    receive: (event: LiveDataEvent) => void
    /** Un `typing` d'un autre : « X écrit… » le temps de `TYPING_THROTTLE_MS`. */
    receiveTyping: (senderId: string | null) => void
    /** Qui écrit, dans le fil ouvert (son id dans la salle) ; `null` : personne. */
    typingId: string | null
    /**
     * Une pièce jointe, dans le fil du tuteur : le formulaire que l'écran a préparé, et son aperçu.
     * `thread` : le fil où le geste a commencé (défaut : le fil ouvert).
     */
    sendAttachment: (
        kind: LiveChatAttachmentKind,
        body: unknown,
        preview: LiveChatAttachmentPreview,
        thread?: string | null
    ) => void
    /**
     * Un vocal : confié à la file des vocaux, sous le nom de fichier donné, dans le fil où
     * l'enregistrement a commencé (`thread`, défaut : le fil ouvert).
     */
    sendVoice: (file: VoiceFile, fileName: string, thread?: string | null) => void
    /** « Renvoyer » un envoi resté en échec (un texte, une pièce ou un vocal). */
    retry: (messageId: string) => void
    /** Une pièce part. */
    attaching: boolean
    /** Il reste des messages plus anciens dans le fil ouvert. */
    hasEarlier: boolean
    loadingEarlier: boolean
    loadEarlier: () => void
}

/** Ce qu'une pièce jointe part comme : la route de la messagerie qui la range. */
export type LiveChatAttachmentKind = "image" | "document"

/** L'aperçu d'une pièce qui part : son adresse locale, le nom d'un document. */
export type LiveChatAttachmentPreview = { uri: string; name?: string }

const ATTACHMENT_SERVICES: Record<LiveChatAttachmentKind, keyof typeof messengerService> = {
    image: "postImage",
    document: "postDocument",
}

/** La page suivante d'un fil, telle que la messagerie la dit (`meta`). */
type Paging = { nextPageId: string | number | null; finalPage: boolean }

type MessengerPage = { meta?: { next_page_id?: string | number | null; final_page?: boolean } } | null | undefined

const pagingOf = (page: MessengerPage): Paging => ({
    nextPageId: page?.meta?.next_page_id ?? null,
    finalPage: page?.meta?.final_page !== false,
})

/** Ce qu'il faut pour renvoyer un message qui n'est pas parti. */
type UnsentMessage = { kind: "text" | LiveChatAttachmentKind; body: unknown }

/** La frappe ne se redit pas avant ce délai : celui où l'indicateur s'efface chez le tuteur. */
export const TYPING_THROTTLE_MS = 1500

type RootState = { data?: { currentUser?: { object?: { messages?: Record<string, unknown> } | null } } }

const NO_MESSAGES: LiveChatMessage[] = []

/**
 * Les messages reçus par le canal que le fil relu ne porte pas encore. Un texte annoncé par le tuteur a
 * l'id de son appareil, pas celui du serveur, et le serveur ne renvoie aucune clé du client : il se
 * reconnaît à son auteur et à son texte, un pour un (deux « ok » reçus, un seul relu : l'un reste). Le
 * texte se compare espaces normalisés des deux côtés : le serveur retire ceux des bords (`TrimStrings`),
 * et un écart d'une espace laissait le message en double toute la séance. Il se compare aussi décodé :
 * la messagerie sert le texte échappé (`l&#039;exercice`), et une apostrophe faisait le même double.
 * Une pièce a l'id du serveur.
 */
export function uncoveredReceived(received: LiveChatMessage[], server: LiveChatMessage[]): LiveChatMessage[] {
    const ids = new Set(server.map((message) => String(message.id)))
    const texts = new Map<string, number>()
    for (const message of server) {
        if (message.body == null) continue
        const key = textKey(message)
        texts.set(key, (texts.get(key) ?? 0) + 1)
    }
    return received.filter((message) => {
        if (ids.has(String(message.id))) return false
        if (message.body == null) return true
        const key = textKey(message)
        const left = texts.get(key) ?? 0
        if (left === 0) return true
        texts.set(key, left - 1)
        return false
    })
}

/** L'auteur et le texte d'un message, décodé, espaces normalisés. */
const textKey = (message: LiveChatMessage) =>
    `${String(message.owner_id)}|${decode(message.body ?? "")
        .trim()
        .replace(/\s+/g, " ")}`

/** Une liste de messages lue du serveur, sans ses trous ; autre chose qu'une liste : aucun. */
const messageList = (raw: unknown): LiveChatMessage[] =>
    Array.isArray(raw) ? (raw as LiveChatMessage[]).filter((item) => item != null) : []

const pageMessages = (page: MessengerPage): LiveChatMessage[] =>
    messageList((page as { data?: unknown } | null | undefined)?.data)

/**
 * Le `newMessage` du canal → un message du fil. Deux formes, celles de `CommonChat` : un texte
 * (`_id`, `text`, `user._id`), ou une pièce jointe — la ressource que le serveur a rangée (`id`,
 * `thread_id`, `owner_id`, `image`, `document`, `audio`).
 */
export function messageOfEvent(event: LiveDataEvent): LiveChatMessage | null {
    const id = event._id
    if (id == null) {
        const serverId = event.id
        if (serverId == null) return null
        const image = event.image as { lg?: string } | null | undefined
        return {
            id: String(serverId),
            thread_id: event.thread_id as string | number | undefined,
            owner_id: event.owner_id as string | number | undefined,
            body: typeof event.body === "string" ? event.body : null,
            created_at: typeof event.created_at === "string" ? event.created_at : moment().toISOString(),
            image: image ?? null,
            document: typeof event.document === "string" ? event.document : null,
            document_name: typeof event.document_name === "string" ? event.document_name : null,
            audio: typeof event.audio === "string" ? event.audio : null,
        }
    }
    const author = (event.user as { _id?: string | number } | undefined)?._id
    const createdAt = event.createdAt
    return {
        id: String(id),
        thread_id: event.threadId as string | number | undefined,
        owner_id: author,
        body: typeof event.text === "string" ? event.text : null,
        created_at: typeof createdAt === "string" ? createdAt : moment().toISOString(),
    }
}

/**
 * Les vocaux de la file, dans un fil : en attente ou en cours → « Envoi... », en échec → « Non
 * envoyé », sur le fichier local ; partis, la ressource que le serveur a rangée (écartée quand le fil
 * relu la porte).
 */
export function voiceMessages(
    items: VoiceOutboxItem<LiveChatVoiceTarget, unknown>[],
    threadId: string | null,
    selfId: string | number | null | undefined
): LiveChatMessage[] {
    if (threadId == null) return []
    return items.flatMap((item): LiveChatMessage[] => {
        if (item.target.threadId !== threadId) return []
        if (item.status === "sent") {
            const resource = item.result != null ? messageOfEvent(item.result as unknown as LiveDataEvent) : null
            return resource != null ? [{ ...resource, thread_id: resource.thread_id ?? threadId }] : []
        }
        return [
            {
                id: item.id,
                thread_id: threadId,
                owner_id: selfId ?? undefined,
                created_at: item.createdAt,
                audio: item.file.uri,
                status: item.status === "failed" ? "failed" : "sending",
            },
        ]
    })
}

export function useLiveChat(options: UseLiveChatOptions): LiveChatController {
    const { session, user, open, channel, send, telemetry, voiceOutbox } = options
    const dispatch = useDispatch()
    const channels = useMemo(() => chatChannels(session), [session])
    const threadId = chatThreadOf(session, channel)
    // Un fil privé n'est dit qu'à son tuteur ; « Tous », à toute la salle.
    const channelTutor = channels.find((entry) => entry.channel === channel)?.tutorId ?? null
    const to = useMemo(() => (channelTutor == null ? null : [Number(channelTutor)]), [channelTutor])
    // Le fil du groupe se lit seulement : rien n'y part, quelle que soit la puce qui l'ouvre.
    const groupThread = session?.thread_id != null ? String(session.thread_id) : null
    const isReadOnlyThread = useCallback(
        (thread: string | null | undefined) => thread != null && thread === groupThread,
        [groupThread]
    )
    const readOnly = isReadOnlyThread(threadId)
    // Le fil où le geste a commencé (sinon le fil ouvert), s'il reçoit : `null` sinon.
    const writableTarget = useCallback(
        (thread: string | null | undefined) => {
            const target = thread !== undefined ? thread : threadId
            return target == null || isReadOnlyThread(target) ? null : target
        },
        [threadId, isReadOnlyThread]
    )
    const voiceQueue = useVoiceOutbox(voiceOutbox)

    const server = useSelector((state: RootState) =>
        threadId != null ? state.data?.currentUser?.object?.messages?.[threadId] : undefined
    )
    const [received, setReceived] = useState<LiveChatMessage[]>([])
    const [pending, setPending] = useState<LiveChatMessage[]>([])
    const [drafts, setDrafts] = useState<Record<string, string>>({})
    const draft = threadId != null ? (drafts[threadId] ?? "") : ""
    const [sending, setSending] = useState(false)
    const [attaching, setAttaching] = useState(false)
    const [typingId, setTypingId] = useState<string | null>(null)
    const [paging, setPaging] = useState<Record<string, Paging>>({})
    /** Les fils dont le premier relevé est revenu, ou a échoué : ils ne montrent plus leur forme. */
    const [settledThreads, setSettledThreads] = useState<Record<string, true>>({})
    const [loadingEarlier, setLoadingEarlier] = useState(false)
    /** Ce qui n'est pas parti, gardé pour « Renvoyer » : le texte, ou la pièce et son formulaire. */
    const unsentRef = useRef(new Map<string, UnsentMessage>())
    const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const lastTypingRef = useRef(0)
    const openRef = useRef(open)
    openRef.current = open
    const threadRef = useRef(threadId)
    threadRef.current = threadId

    const markRead = useCallback(
        (thread: string) => {
            apiCall(dispatch as never, messengerService.getMarkRead, { routeParams: { threadId: thread } }).catch(
                (cause: unknown) => {
                    telemetry.error("chat_mark_read", cause)
                }
            )
        },
        [dispatch, telemetry]
    )

    // Le fil relu n'est dit lu que si le chat est ouvert sur lui, au moment où la relecture revient.
    const refresh = useCallback(
        (thread: string) =>
            apiCall<MessengerPage>(dispatch as never, messengerService.getMessages, {
                routeParams: { threadId: thread },
                whole: true,
            }).then((page) => {
                const relu = pageMessages(page)
                setReceived((list) => {
                    const others = list.filter((message) => String(message.thread_id) !== thread)
                    const here = list.filter((message) => String(message.thread_id) === thread)
                    return [...others, ...uncoveredReceived(here, relu)]
                })
                setPaging((all) => ({ ...all, [thread]: pagingOf(page) }))
                if (openRef.current && threadRef.current === thread) markRead(thread)
            }),
        [dispatch, markRead]
    )

    const threadPaging = threadId != null ? paging[threadId] : undefined
    const hasEarlier = threadPaging != null && !threadPaging.finalPage && threadPaging.nextPageId != null
    const loadEarlier = useCallback(() => {
        // Comme la messagerie : rien tant qu'une lecture court, ni après la dernière page.
        if (threadId == null || loadingEarlier || threadPaging == null || threadPaging.finalPage) return
        if (threadPaging.nextPageId == null) return
        setLoadingEarlier(true)
        apiCall<MessengerPage>(dispatch as never, messengerService.getPageMessages, {
            routeParams: { threadId, pageId: threadPaging.nextPageId },
            whole: true,
        })
            .then((page) => setPaging((all) => ({ ...all, [threadId]: pagingOf(page) })))
            .catch((cause: unknown) => {
                telemetry.error("chat_earlier", cause)
            })
            .finally(() => setLoadingEarlier(false))
    }, [dispatch, threadId, loadingEarlier, threadPaging, telemetry])

    const receiveTyping = useCallback((senderId: string | null) => {
        if (typingTimerRef.current != null) clearTimeout(typingTimerRef.current)
        setTypingId(senderId)
        typingTimerRef.current = setTimeout(() => setTypingId(null), TYPING_THROTTLE_MS)
    }, [])
    useEffect(
        () => () => {
            if (typingTimerRef.current != null) clearTimeout(typingTimerRef.current)
        },
        []
    )

    useEffect(() => {
        if (!open || threadId == null) return
        refresh(threadId)
            .catch((cause: unknown) => {
                telemetry.error("chat_messages", cause)
            })
            .finally(() => setSettledThreads((all) => (all[threadId] ? all : { ...all, [threadId]: true })))
    }, [open, threadId, refresh, telemetry])

    // Ce que l'élève a eu sous les yeux, chat ouvert, est lu. Mais le serveur ne dit lu que ce qu'il a
    // déjà : le tuteur annonce son message sur le canal sans attendre que son envoi soit rangé
    // (`CommonChat.onSend`), et le `mark-read` parti à l'arrivée passe souvent avant lui ; la relecture
    // suivante du compte remontait alors le non-lu. Le fil qui a reçu, chat ouvert, est redit lu quand
    // l'élève le quitte (le chat fermé, l'autre canal, le live quitté).
    const receivedOpenRef = useRef<string | null>(null)
    useEffect(() => {
        if (!open || threadId == null) return undefined
        return () => {
            const thread = receivedOpenRef.current
            receivedOpenRef.current = null
            if (thread != null) markRead(thread)
        }
    }, [open, threadId, markRead])

    const receive = useCallback(
        (event: LiveDataEvent) => {
            const message = messageOfEvent(event)
            if (message == null) return
            setReceived((list) => (list.some((item) => item.id === message.id) ? list : [...list, message]))
            // Son message arrivé, il n'écrit plus.
            setTypingId(null)
            const thread = threadRef.current
            if (openRef.current && thread != null && String(message.thread_id) === thread) {
                markRead(thread)
                receivedOpenRef.current = thread
            }
        },
        [markRead]
    )

    const setDraft = useCallback(
        (text: string) => {
            if (threadId == null || readOnly) return
            setDrafts((all) => ({ ...all, [threadId]: text }))
            if (text === "") return
            const now = Date.now()
            if (now - lastTypingRef.current < TYPING_THROTTLE_MS) return
            lastTypingRef.current = now
            void send("typing", {}, to, "drop")
        },
        [send, to, threadId, readOnly]
    )

    /** Le message rangé par le serveur et relu : il quitte les envois en cours. */
    const settled = useCallback(
        (temporaryId: string) => setPending((list) => list.filter((message) => message.id !== temporaryId)),
        []
    )
    /** Le message n'est pas parti : il le dit, avec ce qu'en a dit le serveur, et garde de quoi « Réessayer ». */
    const failed = useCallback((temporaryId: string, unsent: UnsentMessage, cause: unknown) => {
        unsentRef.current.set(temporaryId, unsent)
        const failure = apiFailureOf(cause) ?? undefined
        setPending((list) =>
            list.map((message) =>
                message.id === temporaryId
                    ? { ...message, status: "failed", ...(failure != null ? { failure } : {}) }
                    : message
            )
        )
    }, [])
    /**
     * Le serveur a rangé le message : il est parti. Il prend la forme que le serveur lui a donnée
     * (son id, sa date) et attend la relecture du fil, qui l'écarte ; si elle échoue, il reste là,
     * envoyé, au lieu de se dire « Non envoyé » et d'être renvoyé en double.
     */
    const stored = useCallback((temporaryId: string, resource: Record<string, unknown> | null, thread: string) => {
        unsentRef.current.delete(temporaryId)
        const saved = resource != null ? messageOfEvent(resource as LiveDataEvent) : null
        setPending((list) =>
            list.map((message) => {
                if (message.id !== temporaryId) return message
                if (saved == null) return { ...message, status: undefined, failure: undefined }
                return { ...saved, thread_id: saved.thread_id ?? thread }
            })
        )
        return saved?.id ?? temporaryId
    }, [])

    /** Un texte part : le serveur le range, puis le canal l'annonce au destinataire. */
    const postText = useCallback(
        (temporaryId: string, thread: string, text: string, createdAt: string) => {
            setSending(true)
            apiCall<Record<string, unknown> | null>(dispatch as never, messengerService.postMessages, {
                routeParams: { threadId: thread },
                data: { message: text, temporary_id: "message" },
                whole: true,
            })
                .then(
                    (resource) => {
                        const id = stored(temporaryId, resource, thread)
                        void send(
                            "newMessage",
                            {
                                _id: temporaryId,
                                text,
                                createdAt,
                                user: { _id: user?.id, name: user?.display_name },
                                threadId: thread,
                            },
                            to,
                            "drop"
                        )
                        return refresh(thread).then(
                            () => settled(id),
                            (cause: unknown) => {
                                telemetry.error("chat_messages", cause)
                            }
                        )
                    },
                    (cause: unknown) => {
                        telemetry.error("chat_send", cause)
                        failed(temporaryId, { kind: "text", body: text }, cause)
                    }
                )
                .finally(() => setSending(false))
        },
        [dispatch, send, refresh, telemetry, user?.id, user?.display_name, to, settled, failed, stored]
    )

    /** Une pièce part : le serveur la range, puis le canal annonce sa ressource au tuteur. */
    const postAttachment = useCallback(
        (temporaryId: string, thread: string, kind: LiveChatAttachmentKind, body: unknown) => {
            setAttaching(true)
            const service = messengerService[ATTACHMENT_SERVICES[kind]] as unknown as Parameters<typeof apiCall>[1]
            apiCall<Record<string, unknown> | null>(dispatch as never, service, {
                routeParams: { threadId: thread },
                data: body,
                whole: true,
            })
                .then(
                    (resource) => {
                        const id = stored(temporaryId, resource, thread)
                        if (resource != null) void send("newMessage", resource, to, "drop")
                        return refresh(thread).then(
                            () => settled(id),
                            (cause: unknown) => {
                                telemetry.error("chat_messages", cause)
                            }
                        )
                    },
                    (cause: unknown) => {
                        telemetry.error("chat_attachment", cause, { kind })
                        failed(temporaryId, { kind, body }, cause)
                    }
                )
                .finally(() => setAttaching(false))
        },
        [dispatch, send, refresh, telemetry, to, settled, failed, stored]
    )

    const sendAttachment = useCallback(
        (kind: LiveChatAttachmentKind, body: unknown, preview: LiveChatAttachmentPreview, thread?: string | null) => {
            const target = writableTarget(thread)
            if (target == null) return
            const temporaryId = `pending-${kind}-${Date.now()}`
            const optimistic: LiveChatMessage = {
                id: temporaryId,
                thread_id: target,
                owner_id: user?.id ?? undefined,
                created_at: moment().toISOString(),
                status: "sending",
                ...(kind === "image" ? { image: { lg: preview.uri } } : {}),
                ...(kind === "document" ? { document: preview.uri, document_name: preview.name ?? null } : {}),
            }
            setPending((list) => [...list, optimistic])
            postAttachment(temporaryId, target, kind, body)
        },
        [writableTarget, user?.id, postAttachment]
    )

    const sendVoice = useCallback(
        (file: VoiceFile, fileName: string, thread?: string | null) => {
            const target = writableTarget(thread)
            if (target == null) return
            voiceOutbox.enqueue(file, { threadId: target, fileName })
        },
        [writableTarget, voiceOutbox]
    )

    // Un vocal parti pendant que le live est monté s'annonce au tuteur de son fil, comme une pièce. Ceux déjà
    // partis à l'ouverture du live (la messagerie, plus tôt) ne se réannoncent pas.
    const announcedRef = useRef<Set<string> | null>(null)
    if (announcedRef.current == null) {
        announcedRef.current = new Set(
            voiceOutbox
                .getState()
                .items.filter((item) => item.status === "sent")
                .map((item) => item.id)
        )
    }
    useEffect(() => {
        const announced = announcedRef.current
        if (announced == null) return
        for (const item of voiceQueue.items) {
            if (item.status !== "sent" || announced.has(item.id)) continue
            announced.add(item.id)
            const owner = channels.find((entry) => entry.tutorId != null && entry.threadId === item.target.threadId)
            if (owner?.tutorId == null) continue
            const resource = item.result as Record<string, unknown> | null
            if (resource != null) void send("newMessage", resource, [Number(owner.tutorId)], "drop")
            refresh(item.target.threadId).catch((cause: unknown) => {
                telemetry.error("chat_messages", cause)
            })
        }
    }, [voiceQueue.items, channels, send, refresh, telemetry])

    const retry = useCallback(
        (messageId: string) => {
            const voice = voiceQueue.items.find((item) => item.id === messageId)
            if (voice != null) {
                if (!isReadOnlyThread(voice.target.threadId)) voiceOutbox.retry(messageId)
                return
            }
            const unsent = unsentRef.current.get(messageId)
            const message = pending.find((item) => item.id === messageId && item.status === "failed")
            if (unsent == null || message == null || message.thread_id == null) return
            if (isReadOnlyThread(String(message.thread_id))) return
            setPending((list) =>
                list.map((item) => (item.id === messageId ? { ...item, status: "sending", failure: undefined } : item))
            )
            const thread = String(message.thread_id)
            if (unsent.kind === "text") {
                postText(messageId, thread, String(unsent.body), message.created_at ?? moment().toISOString())
            } else {
                postAttachment(messageId, thread, unsent.kind, unsent.body)
            }
        },
        [pending, postText, postAttachment, voiceQueue.items, voiceOutbox, isReadOnlyThread]
    )

    const sendMessage = useCallback(() => {
        const text = draft.trim()
        if (text === "" || threadId == null || readOnly) return
        const temporaryId = `pending-${Date.now()}`
        const createdAt = moment().toISOString()
        const optimistic: LiveChatMessage = {
            id: temporaryId,
            thread_id: threadId,
            owner_id: user?.id ?? undefined,
            body: text,
            created_at: createdAt,
            status: "sending",
        }
        setPending((list) => [...list, optimistic])
        setDrafts((all) => ({ ...all, [threadId]: "" }))
        postText(temporaryId, threadId, text, createdAt)
    }, [draft, threadId, readOnly, user?.id, postText])

    const messages = useMemo(() => {
        const fromServer = messageList(server)
        const known = new Set(fromServer.map((message) => String(message.id)))
        const live = uncoveredReceived(
            received.filter((message) => String(message.thread_id) === threadId),
            fromServer
        )
        // Un envoi rangé que le fil relu porte déjà ne se montre qu'une fois.
        const mine = [
            ...pending.filter((message) => String(message.thread_id) === threadId && !known.has(message.id)),
            ...voiceMessages(voiceQueue.items, threadId, user?.id).filter((message) => !known.has(message.id)),
        ]
        return fromServer.length + live.length + mine.length === 0 ? NO_MESSAGES : [...fromServer, ...live, ...mine]
    }, [server, received, pending, threadId, voiceQueue.items, user?.id])
    const voiceSending = voiceQueue.items.some((item) => item.target.threadId === threadId && item.status === "sending")

    const loading = open && threadId != null && !Array.isArray(server) && settledThreads[threadId] !== true

    return {
        threadId,
        messages,
        loading,
        readOnly,
        draft,
        setDraft,
        sendMessage,
        sending,
        receive,
        receiveTyping,
        typingId,
        sendAttachment,
        sendVoice,
        retry,
        attaching: attaching || voiceSending,
        hasEarlier,
        loadingEarlier,
        loadEarlier,
    }
}
